// ─── Custom Domain Resolution ─────────────────────────────────────────────
// Detects if the app is running on a custom domain and resolves the account.
// Used by App.jsx to scope auth and by Login.jsx for branding.

import { createClient } from "@supabase/supabase-js";

const masterUrl = import.meta.env.VITE_SUPABASE_URL;
const masterAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Default domains that don't need custom domain lookup
const DEFAULT_DOMAINS = [
  "app.clarifico.io",
  "localhost",
  "127.0.0.1",
];

let resolvedDomain = null;

/**
 * Check if the current hostname is a custom domain (not app.clarifico.io or localhost)
 */
export function isCustomDomain() {
  const host = window.location.hostname;
  return !DEFAULT_DOMAINS.some(d => host === d || host.endsWith(`.${d}`));
}

/**
 * Get the current hostname
 */
export function getCurrentHostname() {
  return window.location.hostname;
}

/**
 * Look up which account owns the current custom domain.
 * Returns { account_id, account_slug, domain } or null.
 * Results are cached for the session.
 */
export async function resolveCustomDomain() {
  if (resolvedDomain !== undefined && resolvedDomain !== null) return resolvedDomain;
  if (!isCustomDomain()) {
    resolvedDomain = null;
    return null;
  }

  const hostname = getCurrentHostname();

  try {
    // Resolve via SECURITY DEFINER RPC. Returns theme fields only — never
    // account_id or secrets — so the anon key can call it without leaking
    // tenant identifiers. Replaces the old direct custom_domains table select.
    const res = await fetch(
      `${masterUrl}/rest/v1/rpc/resolve_domain_theme`,
      {
        method: "POST",
        headers: {
          "apikey": masterAnonKey,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ p_hostname: hostname }),
      }
    );

    const rows = await res.json();
    const match = Array.isArray(rows) ? rows?.[0] : null;

    if (match) {
      resolvedDomain = {
        domain: hostname,
        account: {
          slug: match.account_slug,
          name: match.account_name,
          theme_primary_color: match.theme_primary_color,
          theme_logo_url: match.theme_logo_url,
          widget_color: match.widget_color,
        },
      };
      return resolvedDomain;
    }
  } catch (err) {
    console.warn("Custom domain resolution failed:", err);
  }

  resolvedDomain = null;
  return null;
}

/**
 * Get the cached resolved domain (call resolveCustomDomain first)
 */
export function getResolvedDomain() {
  return resolvedDomain;
}

/**
 * Clear the cached resolution (for testing or domain changes)
 */
export function clearDomainCache() {
  resolvedDomain = null;
}
